import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { AuthService } from '../../../user/auth.service';
import { HeaderService } from '../poll-header/header.service';
import { NavigationService } from '../../core/navigation.service';
import { CurrentPollService } from '../../core/current-poll.service';
import { CalendarEvent, CalendarScope, HeaderData, OptionPersistence, buildCalendarDescription, calendarEventsFor, calendarLinks } from 'whocan-lib';
import { TemplateService } from '../../core/poll-templates/template.service';
import { environment } from '../../../../environments/environment';
import { PollService } from '../../core/poll.service';
import { GoogleAnalyticsService } from '../../../shared/services/google-analytics.service';
import { UserVoteService } from '../../features/vote/user-vote.service';
import { OptionsService } from '../options/options.service';
import { UserService } from '../../../user/user.service';
import { UIMesageService } from '../../../shared/services/ui-message.service';
import { TranslationGeneralService } from '../../../shared/translation/translation.general.service';
import { isConnectionLost, isNetworkRequestFailed } from '../../core/poll-errors';

/** which dates: all of the poll (owner, context action bar) or the ones the own vote said yes/maybe to */
export type CalendarScopeKind = CalendarScope['kind'];
export type CalendarLinkTarget = 'google' | 'outlook';

const CALENDAR_NAME = 'Whocan.org';
const ICS_DOWNLOAD_EVENTS: Record<CalendarScopeKind, string> = {
    all: 'ical_download_all',
    votedFor: 'ical_download_votes_for',
};

/**
 * Calendar export of the current poll: .ics download and the Google / Outlook links of the add-to-calendar
 * menu (workorder-calendar-quick-wins-a-2026-11-02). Which dates go out and with which data decides
 * calendarEventsFor in whocan-lib alone - this service only hands it the poll data and builds the description.
 * ical-generator, timezones-ical-library and file-saver are loaded on the click, not with the vote page.
 */
@Injectable({
    providedIn: 'root'
})

export class ICalService {
    constructor(
        private logger: LoggerService,
        private userVoteService: UserVoteService,
        private headerService: HeaderService,
        private optionsService: OptionsService,
        private navigationService: NavigationService,
        private authService: AuthService,
        private currentPollService: CurrentPollService,
        private pollService: PollService,
        private templateService: TemplateService,
        private googleAnayltics: GoogleAnalyticsService,
        private userService: UserService,
        private uiMessage: UIMesageService,
        private trans: TranslationGeneralService,
    ) {
    }

    /** context action bar: every date of the poll */
    public downloadAllEvents(): Promise<void> {
        return this.downloadIcs('all');
    }

    public async downloadIcs(kind: CalendarScopeKind): Promise<void> {
        this.googleAnayltics.emitSimpleEvent(ICS_DOWNLOAD_EVENTS[kind])
        if (!(await this.ensureAccessLinkData())) { return; }
        const events = this.eventsForScope(kind);
        if (events.length === 0) { return; }
        try {
            const [{ toIcs }, fileSaver] = await Promise.all([
                import('whocan-lib/poll/calendar/calendar-ics'),
                import('file-saver'),
            ]);
            // file-saver is UMD, module.exports is the function itself: the dynamic import hands it over as default only
            const saveAs = fileSaver.default ?? fileSaver.saveAs;
            const ics = await toIcs(events, {
                description: this.getDescription(),
                texts: { maybeSuffix: this.maybeSuffix, calendarName: CALENDAR_NAME, onlineLabel: this.onlineLabel },
            });
            const blob = new Blob([ics], { type: 'text/calendar' });//https://stackoverflow.com/questions/51231882/javascript-in-mobile-safari-ios-wont-download-calendar-invite
            saveAs(blob, 'whocan.ics');
        } catch (error) {
            // the chunk did not load (offline) or the file could not be built
            this.logger.warn('ICalService: calendar file not created', error);
            this.uiMessage.warning(this.trans.translate(error?.name === 'ChunkLoadError' ? 'authError.networkRequestFailed' : 'internalError'));
        }
    }

    /**
     * Opens the Google / Outlook form of the one event in a new tab. window.open has to run inside the click,
     * otherwise the popup blocker swallows it - so the menu resolves the access link when it opens
     * (prepareAccessLink) and the click only builds the url. If the link is not there yet, the tab is opened
     * empty right away and sent on once it is.
     */
    public openCalendarLink(kind: CalendarScopeKind, target: CalendarLinkTarget): void {
        this.googleAnayltics.emitSimpleEvent(target === 'google' ? 'calendar_link_google' : 'calendar_link_outlook')
        if (this.accessLinkPrepared) {
            const url = this.calendarLink(kind, target);
            if (url) window.open(url, '_blank', 'noopener');
            return;
        }
        const tab = window.open('', '_blank');
        if (tab) tab.opener = null;
        this.ensureAccessLinkData().then(resolved => {
            const url = resolved ? this.calendarLink(kind, target) : null;
            if (!tab) return;
            if (url) tab.location.href = url;
            else tab.close();
        });
    }

    /** set once the access link of the current menu opening is resolved - openCalendarLink can then open synchronously */
    private accessLinkPrepared = false;

    /** the add-to-calendar menu opens: resolve the access link now, without a message (the click shows one if it still fails) */
    public prepareAccessLink(): void {
        this.accessLinkPrepared = false;
        this.resolveAccessLink().then(
            () => this.accessLinkPrepared = true,
            error => this.logger.warn('ICalService: access link not prepared for the calendar menu', error),
        );
    }

    private calendarLink(kind: CalendarScopeKind, target: CalendarLinkTarget): string | null {
        const [event] = this.eventsForScope(kind);
        if (!event) return null;
        return calendarLinks(event, { description: this.getDescription(), maybeSuffix: this.maybeSuffix, onlineLabel: this.onlineLabel })[target];
    }

    /** the access link of the anonymous user for the event description (ensureAccessLinkData) */
    private accessUrl: string | null = null;
    /** which link accessUrl is - the description names it (workorder 1.12); null: plain url, no link name */
    private accessLinkKind: 'poll' | 'vote' | null = null;

    /**
     * The event description of an anonymous user carries a join link (getUrl): owner / co-owner get the
     * organizer link (ownerKey - the one poll key since workorder 1.9; the calendar carries the same link as
     * the dialog and the mails, concept §0 revision 2026-10-18), a voter the key of his answer; whoever holds
     * nothing gets the plain vote url. Resolved once before the calendar is built synchronously, after the
     * own user record is there (the signal that the sign-in has settled,
     * workorder-auth-window-option-access-link-2026-10-04, task 3); false (with a message) when that fails.
     */
    private async ensureAccessLinkData(): Promise<boolean> {
        try {
            await this.resolveAccessLink();
            return true;
        } catch (error) {
            this.logger.warn('ICalService: access link not resolved, no calendar download', error);
            const connectionProblem = isConnectionLost(error) || isNetworkRequestFailed(error);
            this.uiMessage.warning(this.trans.translate(connectionProblem ? 'authError.networkRequestFailed' : 'internalError'));
            return false;
        }
    }

    private async resolveAccessLink(): Promise<void> {
        if (!this.authService.isAnonymous) { return; }
        await this.userService.whenOwnDbUserLoaded(this.authService.uid);
        // a poll link opens the preview without the settings: whoever comes from the calendar wants to see
        // the event (workorder 1.8); a vote link keeps the vote page
        const parts = await this.navigationService.getJoinLinkParts(this.navigationService.getPathPollPreview());
        this.accessUrl = parts ? this.navigationService.composeJoinUrl(parts) : this.navigationService.getUrlVote();
        this.accessLinkKind = parts?.kind ?? null;
        // never the url itself: it carries the key
        this.logger.log('ICalService: access link resolved', parts?.kind ?? 'none');
    }

    public get hasPollDateEvents(): boolean {
        return this.eventsForScope('all').length > 0;
    }

    public get hasDateEventsVotedFor(): boolean {
        return this.eventsForScope('votedFor').length > 0;
    }

    /**
     * The calendar events of the current poll (calendarEventsFor) from the data as it is persisted: the header
     * without the "today 12:00" placeholder of a header that has no date, the option list with its sortMap,
     * the own vote. Empty while the poll is not loaded. Location and meeting link go in exactly when the vote page
     * shows them (templateSettingFlags, calendar quick wins C).
     */
    public eventsForScope(kind: CalendarScopeKind): CalendarEvent[] {
        if (!this.pollService.pollIsLoaded()) { return []; }
        const headerClass = this.headerService.headerClass;
        const headerData = headerClass?.headerData;
        if (!headerData) { return []; }
        const header: HeaderData = { ...headerData };
        if (headerClass.whenIsPlaceholder) { delete header.when; }
        const scope: CalendarScope = kind === 'votedFor'
            ? { kind, vote: this.userVoteService.userVote?.data }
            : { kind };
        const optionList = this.optionsService.optionList;
        const options: OptionPersistence[] = [];
        optionList.forEachIncludingDeleted((optionClass, index) => { options[index] = optionClass.data; });
        return calendarEventsFor({
            pollId: this.currentPollService.pollId, header, options, sortMap: optionList.sortMap, scope,
            templateFlags: this.templateService.templateSettingFlags(),
        });
    }

    /** title suffix of a maybe date - the app's own "if necessary" wording */
    private get maybeSuffix(): string {
        return this.templateService.getLabel('iCal', 'maybeSuffix');
    }

    /** "Online:" in front of the meeting link in the description - the label of the vote page (calendar quick wins C) */
    private get onlineLabel(): string {
        return this.templateService.getLabel('vote', 'online');
    }

    /**
     * The description of the entry - joined and filled by buildCalendarDescription (whocan-lib), the same function
     * the vote confirmation mail uses for its attachment (calendar quick wins B). Which lines and which link stay
     * here: the owner's or the participant's line 1 (the participant's without the organizer's name when the poll
     * has none - never "undefined's event"), line 2 after getLine2Key, the link after getUrl.
     */
    private getDescription(): string {
        const headerClass = this.headerService.headerClass;
        const isOwner = this.currentPollService.isOwner;
        return buildCalendarDescription({
            line1: this.templateService.getLabel('iCal', isOwner ? 'ownerLine1' : 'notOwnerLine1'),
            line1NoOwner: isOwner ? undefined : this.templateService.getLabel('iCal', 'notOwnerLine1NoOwner'),
            line2: this.templateService.getLabel('iCal', this.getLine2Key(isOwner)),
        }, {
            pollDescription: headerClass.pollDescription,
            pollTitle: headerClass.pollTitle,
            ownerName: headerClass.ownerName,
            pollUrl: this.getUrl(),
            brandName: environment.brandName,
        });
    }

    /**
     * The second line names the link the entry carries (workorder 1.12): an anonymous user's join link is
     * the organizer link or the response link, with its explanation sentence; a signed-in user (or an
     * anonymous one who holds no key) gets the plain url (getUrl) and a line without a link name.
     */
    private getLine2Key(isOwner: boolean): string {
        const joinLinkKind = this.authService.isAnonymous && this.accessUrl ? this.accessLinkKind : null;
        if (joinLinkKind === 'poll') return 'ownerLine2Anonymous';
        if (joinLinkKind === 'vote') return 'notOwnerLine2Anonymous';
        return isOwner ? 'ownerLine2' : 'notOwnerLine2';
    }

    private getUrl(): string {
        // anonymous: the join link of ensureAccessLinkData; signed in: the plain url - the account is the access
        if (this.authService.isAnonymous && this.accessUrl) {
            return this.accessUrl;
        }
        return this.currentPollService.isOwner
            ? this.navigationService.getUrlPollResponses()
            : this.navigationService.getUrlVote();
    }
}
